import {
  BoxGeometry,
  EdgesGeometry,
  Line3,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PerspectiveCamera,
  Ray,
  Vector2,
  Vector3,
} from 'three';

import { LineMaterial, LineSegmentsGeometry, LineSegmentsMesh } from '@/lib/three';
import { ISeriesPoint, WidgetObject } from '@/types';

import { BaseCamera } from '../camera';

const _vec = new Vector3();

export interface Point3dOptions {
  color: string;
  canvasSize?: number[];
  seriesPoint?: ISeriesPoint;
}

const DEFAULT_OPACITY = 0.8;
const _ray = new Ray();

type Point = Mesh<BoxGeometry, MeshBasicMaterial>;

export type ClosestElement = { type: string; inx: number; at: number } | false;

export class Point3d {
  static POINT_SIZE = 0.12;

  static BORDER_SIZE = 0.18;

  static create(data: WidgetObject['data'], options: Point3dOptions) {
    const { color, canvasSize = [1000, 1000] } = options;

    const geometry = new BoxGeometry(Point3d.POINT_SIZE, Point3d.POINT_SIZE, Point3d.POINT_SIZE);

    const material = new MeshBasicMaterial({ color, opacity: DEFAULT_OPACITY, transparent: true, depthTest: false });
    const dot = new Mesh(geometry, material);
    dot.position.fromArray(data);

    const border = Point3d.createBorder(color, canvasSize);

    dot.add(border);

    dot.name = dot.uuid;
    dot.userData = { type: 'point3d', ...options };

    return dot;
  }

  static createBorder(color: string, canvasSize: number[]) {
    const geometry = new BoxGeometry(Point3d.BORDER_SIZE, Point3d.BORDER_SIZE, Point3d.BORDER_SIZE);

    // 创建立方体边框的几何形状
    const edges = new LineSegmentsGeometry();
    edges.fromEdgesGeometry(new EdgesGeometry(geometry));
    const lineMat = new LineMaterial({
      color,
      linewidth: 1,
      resolution: new Vector2(...canvasSize),
      depthTest: false,
    });
    const wireframe = new LineSegmentsMesh(edges, lineMat);

    return wireframe;
  }

  /**
   * 更新 3D 点的位置
   * @param target 目标物
   * @param data 待更新位置 [x, y, z] three.js 坐标系
   * @param options
   */
  static update(target: Point, data: WidgetObject['data']) {
    target.position.fromArray(data);
  }

  static changeColor(target: Point, colorHex: string, refreshData = true) {
    target.traverse((object) => {
      if ('material' in object && 'color' in (object as Point).material) {
        (object as Point).material.color.set(colorHex);
      }
    });
    if (refreshData) {
      target.userData.color = colorHex;
    }
  }

  static changeOpacity(target: Point, opacity: number) {
    target.material.opacity = opacity;
  }

  /**
   * 标准映射方法
   * @param data
   * @param matrix
   * @param cameraModel
   * @returns
   */
  static getFusionMapped(data: number[], matrix: Matrix4, cameraModel: BaseCamera): WidgetObject | undefined {
    if (data.length >= 3) {
      const mapped = Point3d.getVectorFusionMapped(_vec.fromArray(data), matrix, cameraModel);

      return mapped
        ? {
            name: 'point2d',
            data: mapped.toArray(),
          }
        : undefined;
    }

    return undefined;
  }

  /**
   * 3D 点坐标的映射，会改变传入点的值
   * @param point 以 Vector3 形式传入，在计算后会被修改
   * @param matrix 外参矩阵
   * @param cameraModel 相机模型
   * @returns 如能成功映射，则返回传入点索引，否则返回 undefined
   */
  static getVectorFusionMapped(point: Vector3, matrix: Matrix4, cameraModel: BaseCamera) {
    cameraModel.project(point.applyMatrix4(matrix));

    if (point.z > 0) {
      point.divideScalar(point.z);

      return point;
    }

    return undefined;
  }

  /**
   * 根据 widget 生成点
   * @param name
   * @param widget
   * @param matrix
   * @param options
   */
  static createFromWidget(name: string, widget: WidgetObject, matrix: Matrix4, options: Point3dOptions): Point {
    const data = Point3d.pointApplyMatrix4(widget.data, matrix);

    const dot = Point3d.create(data, options);
    dot.name = name;

    return dot;
  }

  /**
   * 由 AnnoInstanceItem.widget 数据更新
   * @param target 更新目标
   * @param widget
   * @param matrix 从点云文件坐标系 到 three.js 坐标系的转换矩阵
   */
  static updateFromWidget(target: Point, widget: WidgetObject, matrix: Matrix4) {
    const data = Point3d.pointApplyMatrix4(widget.data, matrix);
    Point3d.update(target, data);
  }

  /**
   * 导出Widget格式数据
   * @param target
   * @param matrix 从 threejs 到文件坐标系
   */
  static transOutWidget(target: Point, matrix: Matrix4): WidgetObject {
    const data = Point3d.pointApplyMatrix4(Point3d.getVertexFromObject3D(target), matrix);
    return {
      name: 'point3d',
      data,
    };
  }

  static pointApplyMatrix4(data: number[] | ArrayLike<number>, matrix: Matrix4) {
    const vec = new Vector3();
    const newData = new Array(3);
    vec.fromArray(data).applyMatrix4(matrix).toArray(newData);

    return newData;
  }

  static getVertexFromObject3D(target: Point) {
    return target.position.toArray();
  }

  static updateRay(camera: OrthographicCamera | PerspectiveCamera, point: Vector3) {
    if (camera instanceof PerspectiveCamera) {
      _ray.origin.setFromMatrixPosition(camera.matrixWorld);
      _ray.direction.copy(point).sub(_ray.origin).normalize();
    } else if (camera instanceof OrthographicCamera) {
      _ray.origin.copy(point);
      _ray.direction.set(0, 0, -1).transformDirection(camera.matrixWorld);
    }
  }

  /**
   * 查找折线上可响应的点或线
   * @param camera
   * @param point
   * @param vectors
   * @param lines
   * @param detection
   */
  static findClosestElement(
    camera: OrthographicCamera | PerspectiveCamera,
    point: Vector3,
    vectors: Vector3[],
    lines: Line3[],
    detection: number
  ): ClosestElement {
    this.updateRay(camera, point);
    // 遍历折线所有端点，查找阈值内端点
    const vid = vectors.findIndex((v) => _ray.distanceSqToPoint(v) < detection);
    if (vid >= 0) {
      return { type: 'vector', inx: vid, at: 0 };
    }

    const _lineClosest = new Vector3();
    // 遍历折线所有线段
    const lid = lines.findIndex(
      (line) => _ray.distanceSqToSegment(line.start, line.end, undefined, _lineClosest) < detection
    );
    if (lid >= 0) {
      const line = lines[lid];
      const at = _lineClosest.distanceTo(line.start) / line.start.distanceTo(line.end);
      return { type: 'line', inx: lid, at };
    }
    return false;
  }
}
